import { createContext, useContext, useEffect, useState } from "react";

export type AuthUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  tenantId: string;
  twoFactorEnabled: boolean;
};

export type UserPermissions = {
  accessibleGroups: string[];
  permissionsByGroup: Map<string, Set<string>>;
  accessibleTenantIds: string[];
  isPlatformSuperAdmin: boolean;
};

type AuthState = {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: UserPermissions | null;
};

type LoginCredentials = {
  email: string;
  password: string;
};

type TwoFactorSetup = {
  manualEntryKey: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
};

type LoginResult =
  | { status: "authenticated" }
  | { status: "two-factor-required"; challengeToken: string; user: AuthUser }
  | { status: "two-factor-setup-required"; setupToken: string; setup: TwoFactorSetup; user: AuthUser };

type AuthContextType = AuthState & {
  login: (credentials: LoginCredentials) => Promise<LoginResult>;
  verifyTwoFactor: (challengeToken: string, code: string) => Promise<void>;
  completeTwoFactorSetup: (setupToken: string, code: string) => Promise<void>;
  logout: () => void;
  apiClient: ApiClient;
  hasPermission: (permission: string) => boolean;
  refreshAuth: () => Promise<void>;
};

type ApiClientOptions = RequestInit & {
  skipAuth?: boolean;
};

type ApiClient = {
  get: <T>(path: string, options?: ApiClientOptions) => Promise<T>;
  post: <T>(path: string, body: unknown, options?: ApiClientOptions) => Promise<T>;
  put: <T>(path: string, body: unknown, options?: ApiClientOptions) => Promise<T>;
  patch: <T>(path: string, body: unknown, options?: ApiClientOptions) => Promise<T>;
  delete: <T>(path: string, options?: ApiClientOptions) => Promise<T>;
};

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = "auth_token";
const USER_KEY = "auth_user";

const parsePermissions = (data: { groupId: string; permissions: string[] }[]): Map<string, Set<string>> => {
  const map = new Map<string, Set<string>>();

  for (const { groupId, permissions } of data) {
    map.set(groupId, new Set(permissions));
  }

  return map;
};

const createApiClient = (getToken: () => string | null, onUnauthorized: () => void): ApiClient => {
  const request = async <T,>(path: string, options: ApiClientOptions = {}): Promise<T> => {
    const { skipAuth, ...fetchOptions } = options;
    const token = getToken();

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...fetchOptions.headers,
    };

    if (!skipAuth && token) {
      (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
    }

    const res = await fetch(path, {
      ...fetchOptions,
      headers,
    });

    if (res.status === 204) {
      return undefined as T;
    }

    const data = await res.json();

    if (!res.ok) {
      if (res.status === 401) {
        onUnauthorized();
      }

      throw new ApiError(data.error || "Request failed", res.status);
    }

    return data;
  };

  return {
    get: <T,>(path: string, options?: ApiClientOptions) => request<T>(path, { ...options, method: "GET" }),
    post: <T,>(path: string, body: unknown, options?: ApiClientOptions) =>
      request<T>(path, { ...options, method: "POST", body: JSON.stringify(body) }),
    put: <T,>(path: string, body: unknown, options?: ApiClientOptions) =>
      request<T>(path, { ...options, method: "PUT", body: JSON.stringify(body) }),
    patch: <T,>(path: string, body: unknown, options?: ApiClientOptions) =>
      request<T>(path, { ...options, method: "PATCH", body: JSON.stringify(body) }),
    delete: <T,>(path: string, options?: ApiClientOptions) => request<T>(path, { ...options, method: "DELETE" }),
  };
};

type MeResponse = {
  user: AuthUser;
  accessibleGroups: string[];
  accessibleTenantIds: string[];
  isPlatformSuperAdmin: boolean;
  permissions: { groupId: string; permissions: string[] }[];
};

type AuthResponse = {
  token: string;
  user: AuthUser;
};

type Props = {
  children: React.ReactNode;
};

const clearStoredAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

export const AuthProvider = ({ children }: Props) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
    permissions: null,
  });

  const handleUnauthorized = () => {
    clearStoredAuth();
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      permissions: null,
    });
  };

  const apiClient = createApiClient(() => state.token, handleUnauthorized);

  const applyAuthenticatedSession = async (auth: AuthResponse) => {
    localStorage.setItem(TOKEN_KEY, auth.token);
    localStorage.setItem(USER_KEY, JSON.stringify(auth.user));

    const meRes = await fetch("/api/auth/me", {
      headers: { Authorization: `Bearer ${auth.token}` },
    });

    let permissions: UserPermissions | null = null;
    if (meRes.ok) {
      const meData = (await meRes.json()) as MeResponse;
      permissions = {
        accessibleGroups: meData.accessibleGroups || [],
        permissionsByGroup: parsePermissions(meData.permissions || []),
        accessibleTenantIds: meData.accessibleTenantIds || [],
        isPlatformSuperAdmin: meData.isPlatformSuperAdmin || false,
      };
    }

    setState({
      user: auth.user,
      token: auth.token,
      isAuthenticated: true,
      isLoading: false,
      permissions,
    });
  };

  const refreshAuth = async () => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);

    if (!storedToken || !storedUser) {
      setState((current) => ({
        ...current,
        isAuthenticated: false,
        isLoading: false,
        token: null,
        user: null,
        permissions: null,
      }));
      return;
    }

    try {
      const res = await fetch("/api/auth/me", {
        headers: { Authorization: `Bearer ${storedToken}` },
      });

      if (!res.ok) {
        handleUnauthorized();
        return;
      }

      const data = (await res.json()) as MeResponse;
      setState({
        user: data.user,
        token: storedToken,
        isAuthenticated: true,
        isLoading: false,
        permissions: {
          accessibleGroups: data.accessibleGroups || [],
          permissionsByGroup: parsePermissions(data.permissions || []),
          accessibleTenantIds: data.accessibleTenantIds || [],
          isPlatformSuperAdmin: data.isPlatformSuperAdmin || false,
        },
      });
    } catch {
      setState({
        user: JSON.parse(storedUser) as AuthUser,
        token: storedToken,
        isAuthenticated: true,
        isLoading: false,
        permissions: null,
      });
    }
  };

  const login = async (credentials: LoginCredentials): Promise<LoginResult> => {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(credentials),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || "Login failed");
    }

    if (data.twoFactorRequired) {
      return {
        status: "two-factor-required",
        challengeToken: data.challengeToken,
        user: data.user,
      };
    }

    if (data.twoFactorSetupRequired) {
      return {
        status: "two-factor-setup-required",
        setupToken: data.setupToken,
        setup: data.setup,
        user: data.user,
      };
    }

    await applyAuthenticatedSession(data as AuthResponse);
    return { status: "authenticated" };
  };

  const verifyTwoFactor = async (challengeToken: string, code: string): Promise<void> => {
    const response = await fetch("/api/auth/verify-2fa", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ challengeToken, code }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Two-factor verification failed");
    }

    await applyAuthenticatedSession(data as AuthResponse);
  };

  const completeTwoFactorSetup = async (setupToken: string, code: string): Promise<void> => {
    const response = await fetch("/api/auth/2fa/enable", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ setupToken, code }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Two-factor setup failed");
    }

    if ("token" in data) {
      await applyAuthenticatedSession(data as AuthResponse);
      return;
    }

    await refreshAuth();
  };

  const logout = () => {
    clearStoredAuth();
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      permissions: null,
    });
  };

  useEffect(() => {
    refreshAuth();
  }, []);

  const hasPermission = (permission: string): boolean => {
    if (!state.permissions) {
      return false;
    }

    for (const [, perms] of state.permissions.permissionsByGroup) {
      if (perms.has("*")) {
        return true;
      }

      if (perms.has(permission)) {
        return true;
      }

      const [category] = permission.split(".");
      if (perms.has(`${category}.*`)) {
        return true;
      }
    }

    return false;
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        verifyTwoFactor,
        completeTwoFactorSetup,
        logout,
        apiClient,
        hasPermission,
        refreshAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const useApiClient = (): ApiClient => {
  const { apiClient } = useAuth();
  return apiClient;
};
